import * as THREE from 'three';
import * as GaussianSplats3D from '@mkkellogg/gaussian-splats-3d';
import { gsap } from 'gsap';
import { MapNavigationControls } from './controls/MapNavigationControls.js';
import { TurntableControls } from './controls/TurntableControls.js';
import { ChunkManager } from './chunks/ChunkManager.js';
import { FloorPlane } from './scene/FloorPlane.js';
import { DEMOS, DEFAULT_CONFIG } from './config/demos.js';

class App {
  constructor() {
    this.homePage = document.getElementById('home-page');
    this.viewerPage = document.getElementById('viewer-page');
    this.demosGrid = document.getElementById('demos-grid');
    this.backBtn = document.getElementById('back-btn');
    this.controlsList = document.getElementById('controls-list');
    this.arButton = document.getElementById('ar-button');
    
    this.viewer = null;
    this.currentDemo = null;
    
    this.init();
  }

  init() {
    // Demo cards are now rendered by inline script in index.html (with admin edit buttons)
    // this.renderDemoCards();
    this.setupBackButton();
    this.checkARSupport();
  }

  renderDemoCards() {
    this.demosGrid.innerHTML = DEMOS.map(demo => `
      <a href="viewer.html?splat=${encodeURIComponent(demo.splatFile)}" class="demo-card">
        <div class="demo-card-image">
          ${demo.thumbnail 
            ? `<img src="${demo.thumbnail}" alt="${demo.name}">`
            : `<span class="placeholder">🎬</span>`
          }
          <span class="demo-card-badge">${demo.badge}</span>
        </div>
        <div class="demo-card-content">
          <h3>${demo.name}</h3>
          <p>${demo.description}</p>
        </div>
        <div class="demo-card-footer">
          <span class="mode">
            ${demo.mode === 'turntable' 
              ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>'
              : '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>'
            }
            ${demo.mode === 'turntable' ? 'Draai & Zoom' : 'Loop door'}
          </span>
          <span class="btn">Bekijken</span>
        </div>
      </a>
    `).join('');
  }

  setupBackButton() {
    this.backBtn.addEventListener('click', () => this.closeViewer());
  }

  checkARSupport() {
    if ('xr' in navigator) {
      navigator.xr.isSessionSupported('immersive-ar').then(supported => {
        if (supported) {
          this.arButton.classList.remove('hidden');
          this.arButton.addEventListener('click', () => this.startAR());
        }
      });
    }
  }

  async openViewer(demo) {
    this.currentDemo = demo;
    this.homePage.style.display = 'none';
    this.viewerPage.classList.add('active');
    
    // Initialize viewer
    if (!this.viewer) {
      this.viewer = new GaussianSplatViewer(demo);
    } else {
      await this.viewer.loadDemo(demo);
    }
  }

  closeViewer() {
    this.viewerPage.classList.remove('active');
    this.homePage.style.display = 'flex';
    
    if (this.viewer) {
      this.viewer.dispose();
      this.viewer = null;
    }
    this.currentDemo = null;
  }

  startAR() {
    // AR implementation would go here
    // For now, show a message
    alert('AR functionaliteit komt binnenkort! Dit vereist WebXR ondersteuning.');
  }
}

class GaussianSplatViewer {
  constructor(demo) {
    this.container = document.getElementById('canvas-container');
    this.loadingOverlay = document.getElementById('loading-overlay');
    this.progressFill = document.getElementById('progress-fill');
    this.loadingText = document.getElementById('loading-text');
    this.moveIndicator = document.getElementById('move-indicator');
    this.controlsList = document.getElementById('controls-list');
    
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.gsViewer = null;
    this.controls = null;
    this.chunkManager = null;
    this.floorPlane = null;
    this.dome = null;
    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();
    
    this.config = null;
    this.mode = 'walkthrough';
    this.isMoving = false;
    this.clock = new THREE.Clock();
    this.frameCount = 0;
    this.lastFpsUpdate = 0;
    this.animationId = null;
    
    this.init(demo);
  }

  async init(demo) {
    this.setupRenderer();
    this.setupCamera();
    this.setupScene();
    this.animate();
    
    await this.loadDemo(demo);
  }

  setupRenderer() {
    // gsViewer creates its own renderer, we don't need one
    window.addEventListener('resize', () => this.onResize());
  }

  setupCamera() {
    this.camera = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    this.camera.position.set(0, 2, 5);
    this.camera.lookAt(0, 0, 0);
  }

  setupScene() {
    this.scene = new THREE.Scene();
    
    // Add ambient light
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    this.scene.add(ambientLight);
    
    // Add floor plane for raycasting (invisible but clickable)
    this.floorPlane = new FloorPlane(100, 100);
    this.scene.add(this.floorPlane.mesh);
    
    // Add grid helper for visual reference
    const gridHelper = new THREE.GridHelper(50, 50, 0x444444, 0x333333);
    gridHelper.position.y = -0.01;
    this.scene.add(gridHelper);
  }

  setupControls(mode) {
    // Remove old controls
    if (this.controls) {
      this.controls.dispose?.();
    }
    
    // Get the renderer's domElement from gsViewer
    const domElement = this.gsViewer?.renderer?.domElement || this.container;
    
    // Remove old event listeners
    if (this.lastDomElement) {
      this.lastDomElement.removeEventListener('click', this.boundClickHandler);
      this.lastDomElement.removeEventListener('touchend', this.boundTouchHandler);
    }
    window.removeEventListener('keydown', this.boundKeyDownHandler);
    window.removeEventListener('keyup', this.boundKeyUpHandler);
    
    this.lastDomElement = domElement;
    
    this.mode = mode;
    
    if (mode === 'turntable') {
      this.controls = new TurntableControls(this.camera, domElement);
      this.updateControlsUI('turntable');
    } else {
      this.controls = new MapNavigationControls(this.camera, domElement);
      
      // Setup click-to-move for walkthrough
      this.boundClickHandler = (e) => this.onClickToMove(e);
      this.boundTouchHandler = (e) => this.onTouchToMove(e);
      this.boundKeyDownHandler = (e) => this.controls.onKeyDown(e);
      this.boundKeyUpHandler = (e) => this.controls.onKeyUp(e);
      
      domElement.addEventListener('click', this.boundClickHandler);
      domElement.addEventListener('touchend', this.boundTouchHandler);
      window.addEventListener('keydown', this.boundKeyDownHandler);
      window.addEventListener('keyup', this.boundKeyUpHandler);
      
      this.updateControlsUI('walkthrough');
    }
  }

  updateControlsUI(mode) {
    if (mode === 'turntable') {
      this.controlsList.innerHTML = `
        <li><span class="key">Slepen</span> om te draaien</li>
        <li><span class="key">Scroll/Pinch</span> om te zoomen</li>
        <li><span class="key">Rechts slepen</span> om te pannen</li>
      `;
    } else {
      this.controlsList.innerHTML = `
        <li><span class="key">Klik/Tap</span> op vloer om te lopen</li>
        <li><span class="key">Slepen</span> om te draaien</li>
        <li><span class="key">Scroll/Pinch</span> om te zoomen</li>
        <li><span class="key">WASD</span> toetsen om te bewegen</li>
        <li><span class="key">Q/E</span> omhoog/omlaag</li>
      `;
    }
  }

  async loadDemo(demo) {
    this.showLoading('Scene laden...');
    
    try {
      // Dispose existing viewer
      if (this.gsViewer) {
        this.gsViewer.dispose();
        this.gsViewer = null;
      }
      
      // Load config
      this.config = await this.loadConfig(demo.configFile);
      this.mode = this.config?.mode || demo.mode || 'walkthrough';
      
      // Load splat first (creates the viewer and canvas)
      await this.loadSplat(demo.splatFile);
      
      this.hideLoading();
    } catch (error) {
      console.error('Error loading demo:', error);
      this.loadingText.textContent = 'Fout bij laden: ' + error.message;
    }
  }

  async loadConfig(configUrl) {
    try {
      const response = await fetch(configUrl);
      if (response.ok) {
        const config = await response.json();
        console.log('Loaded config:', config);
        // Deep merge config with defaults
        return {
          ...DEFAULT_CONFIG,
          ...config,
          camera: { ...DEFAULT_CONFIG.camera, ...config.camera },
          scene: { ...DEFAULT_CONFIG.scene, ...config.scene },
          floor: { ...DEFAULT_CONFIG.floor, ...config.floor },
          dome: { ...DEFAULT_CONFIG.dome, ...config.dome }
        };
      }
    } catch (e) {
      console.log('No config file found, using defaults:', e);
    }
    return DEFAULT_CONFIG;
  }

  setupDome(domeConfig) {
    // Disabled - just use default black background
  }

  async loadSplat(url) {
    const filename = url.split('/').pop();
    this.loadingText.textContent = `Laden: ${filename}`;

    console.log('Loading splat:', url);

    try {
      // Use rootElement to place canvas in our container
      this.gsViewer = new GaussianSplats3D.Viewer({
        cameraUp: [0, 1, 0],
        initialCameraPosition: [0, 2, 5],
        initialCameraLookAt: [0, 0, 0],
        rootElement: this.container
      });
      
      await this.gsViewer.addSplatScene(url, {
        splatAlphaRemovalThreshold: 5,
        onProgress: (percent) => {
          this.progressFill.style.width = `${percent}%`;
          this.loadingText.textContent = `Laden: ${Math.round(percent)}%`;
        }
      });

      console.log('Splat loaded successfully');
      
      this.gsViewer.start();
      
      // Get camera for stats
      this.camera = this.gsViewer.camera;
      
    } catch (error) {
      console.error('Error in loadSplat:', error);
      throw error;
    }
  }

  applyConfig(config) {
    if (!config) return;
    
    // Apply camera position
    if (config.camera?.position) {
      const [x, y, z] = config.camera.position;
      this.camera.position.set(x, y, z);
    }
    
    // Apply camera lookAt
    if (config.camera?.lookAt) {
      const [x, y, z] = config.camera.lookAt;
      this.camera.lookAt(x, y, z);
    }
    
    // Update controls
    if (this.controls.updateSphericalFromCamera) {
      this.controls.updateSphericalFromCamera();
    }
  }

  onClickToMove(event) {
    if (this.mode !== 'walkthrough') return;
    if (this.controls.wasActualDrag) return;
    
    this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
    this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
    
    this.performRaycastMove();
  }

  onTouchToMove(event) {
    if (this.mode !== 'walkthrough') return;
    if (this.controls.wasActualDrag || this.controls.isPinching) return;
    if (event.changedTouches.length !== 1) return;
    
    const touch = event.changedTouches[0];
    this.mouse.x = (touch.clientX / window.innerWidth) * 2 - 1;
    this.mouse.y = -(touch.clientY / window.innerHeight) * 2 + 1;
    
    this.performRaycastMove();
  }

  performRaycastMove() {
    this.raycaster.setFromCamera(this.mouse, this.camera);
    const intersects = this.raycaster.intersectObject(this.floorPlane.mesh);
    
    if (intersects.length > 0) {
      const targetPoint = intersects[0].point;
      this.moveToPosition(targetPoint);
      this.showMoveIndicator(intersects[0].point);
    }
  }

  moveToPosition(targetPoint) {
    if (this.isMoving) return;
    this.isMoving = true;
    
    const targetCameraPos = new THREE.Vector3(
      targetPoint.x,
      this.camera.position.y,
      targetPoint.z
    );
    
    const offset = new THREE.Vector3().subVectors(
      this.camera.position,
      this.controls.target
    );
    
    gsap.to(this.camera.position, {
      x: targetCameraPos.x,
      z: targetCameraPos.z,
      duration: 1.5,
      ease: 'power2.inOut',
      onUpdate: () => {
        this.controls.target.set(
          this.camera.position.x - offset.x,
          this.controls.target.y,
          this.camera.position.z - offset.z
        );
        this.controls.updateSphericalFromCamera();
      },
      onComplete: () => {
        this.isMoving = false;
      }
    });
  }

  showMoveIndicator(point) {
    const screenPos = point.clone().project(this.camera);
    const x = (screenPos.x * 0.5 + 0.5) * window.innerWidth;
    const y = (-screenPos.y * 0.5 + 0.5) * window.innerHeight;
    
    this.moveIndicator.style.left = `${x}px`;
    this.moveIndicator.style.top = `${y}px`;
    this.moveIndicator.classList.remove('active');
    void this.moveIndicator.offsetWidth;
    this.moveIndicator.classList.add('active');
  }

  onResize() {
    const width = window.innerWidth;
    const height = window.innerHeight;
    
    if (this.camera) {
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
    }
  }

  updateStats() {
    const fpsElement = document.getElementById('fps');
    const splatCountElement = document.getElementById('splat-count');
    const camXElement = document.getElementById('cam-x');
    const camYElement = document.getElementById('cam-y');
    const camZElement = document.getElementById('cam-z');
    
    this.frameCount++;
    const elapsed = this.clock.getElapsedTime();
    if (elapsed - this.lastFpsUpdate >= 1) {
      const fps = Math.round(this.frameCount / (elapsed - this.lastFpsUpdate));
      if (fpsElement) fpsElement.textContent = fps;
      this.frameCount = 0;
      this.lastFpsUpdate = elapsed;
    }
    
    if (this.gsViewer && this.gsViewer.splatMesh) {
      const count = this.gsViewer.splatMesh.getSplatCount();
      if (splatCountElement) splatCountElement.textContent = this.formatNumber(count);
    }
    
    if (camXElement) camXElement.textContent = this.camera.position.x.toFixed(2);
    if (camYElement) camYElement.textContent = this.camera.position.y.toFixed(2);
    if (camZElement) camZElement.textContent = this.camera.position.z.toFixed(2);
  }

  formatNumber(num) {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  }

  showLoading(text = 'Laden...') {
    this.loadingOverlay.classList.remove('hidden');
    this.loadingText.textContent = text;
    this.progressFill.style.width = '0%';
  }

  hideLoading() {
    this.loadingOverlay.classList.add('hidden');
  }

  animate() {
    this.animationId = requestAnimationFrame(() => this.animate());
    
    const delta = this.clock.getDelta();
    
    if (this.controls) {
      this.controls.update(delta);
    }
    
    if (this.chunkManager) {
      this.chunkManager.update();
    }
    
    // gsViewer handles its own rendering in selfDrivenMode
    
    this.updateStats();
  }

  dispose() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    
    if (this.gsViewer) {
      this.gsViewer.dispose();
    }
    
    if (this.controls) {
      this.controls.dispose?.();
    }
    
    if (this.dome) {
      this.scene.remove(this.dome);
      this.dome.geometry.dispose();
      this.dome.material.dispose();
    }
    
    if (this.renderer) {
      this.container.removeChild(this.renderer.domElement);
      this.renderer.dispose();
    }
    
    window.removeEventListener('resize', this.onResize);
  }
}

// Initialize app when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new App();
});
